import { useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Box, TextField } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import MaskedInput from '../../../../../components/Utils/MaskedInput';

const InputsSessao = forwardRef(({ valorDisponivel, mostrarSimulacao, loadValorDisponivel }, ref) => {
  const [valorEscolhido, setValorEscolhido] = useState('');
  const [valorObservacao, setValorObservacao] = useState('');
  const [erroValorEscolhido, setErroValorEscolhido] = useState(false);
  const [erroValorAcimaEscolhido, setErroValorAcimaEscolhido] = useState(false);

  const toNumber = (s, { centsFallback = false } = {}) => {
    const raw = String(s ?? '').trim();
    if (!raw) return NaN;

    const only = raw.replace(/[^\d,.-]/g, '');

    if (/[,.]/.test(only)) {
      const normalized = only.replace(/\./g, '').replace(',', '.');
      const n = parseFloat(normalized);
      return Number.isFinite(n) ? n : NaN;
    }

    if (centsFallback) {
      const neg = /^-/.test(only);
      const digits = only.replace(/\D/g, '');
      if (!digits) return NaN;
      let n = parseInt(digits, 10) / 100;
      if (neg) n = -n;
      return Number.isFinite(n) ? n : NaN;
    }

    const n = parseFloat(only);
    return Number.isFinite(n) ? n : NaN;
  };

  const disponivelNum = useMemo(
    () => toNumber(valorDisponivel, { centsFallback: false }),
    [valorDisponivel]
  );

  const validarValorEscolhido = (rawValue = valorEscolhido) => {
    const isEmpty = rawValue === '' || rawValue === null;
    const escolhidoNum = toNumber(rawValue, { centsFallback: true });
    const temNumeroValido = Number.isFinite(escolhidoNum) && escolhidoNum > 0;
    const temDisponivel = Number.isFinite(disponivelNum) && disponivelNum >= 0;
    const erroBasico = !isEmpty && !temNumeroValido;
    const acima = !isEmpty && temNumeroValido && temDisponivel && disponivelNum < escolhidoNum;
    setErroValorEscolhido(erroBasico);
    setErroValorAcimaEscolhido(acima);
    return temNumeroValido;
  };

  const handleOnChangeValorEscolhido = e => {
    const next = e.target.value;
    setValorEscolhido(next);
    validarValorEscolhido(next);
  };

  const handleOnChangeObservacao = e => {
    setValorObservacao(e.target.value);
  };

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      valorEscolhido,
      valorObservacao
    }),
    validarValorEscolhido,
    getValorEscolhido: () => toNumber(valorEscolhido, { centsFallback: true }),
    setValorEscolhido: novoValor => setValorEscolhido(novoValor ?? ''),
    setValorObservacao: novaObs => setValorObservacao(novaObs ?? '')
  }));

  const placeholderValor =
    mostrarSimulacao && !valorEscolhido ? 'Não informado' : 'Digite apenas os números';

  const placeholderObservacao =
    mostrarSimulacao && !valorObservacao
      ? 'Não informado'
      : 'Comente observações e se houve novos acordos';

  return (
    <>
      <Box className="box-valor-sessao">
        <MaskedInput
          label="Qual o valor que precisa liberar?"
          placeholder={placeholderValor}
          className={`input-valor-sessao ${erroValorAcimaEscolhido ? 'valor-acima' : ''}`}
          fullWidth
          error={erroValorEscolhido}
          helperText={
            erroValorAcimaEscolhido
              ? 'Ao pedir esse valor é possível que sua operação seja aceita parcialmente'
              : erroValorEscolhido
                ? 'Informe um valor válido'
                : ''
          }
          name="valorEscolhido"
          value={valorEscolhido}
          onChange={handleOnChangeValorEscolhido}
          onBlur={() => validarValorEscolhido()}
          InputLabelProps={{ shrink: true }}
          getFormat="money"
          prefixo="R$"
          disabled={mostrarSimulacao || loadValorDisponivel}
        />

        {erroValorAcimaEscolhido && (
          <Box className="erro-valor-acima">
            <ErrorIcon />
          </Box>
        )}
      </Box>

      <Box className="box-detalhes-sessao">
        <TextField
          className="input-valor-sessao"
          value={valorObservacao}
          onChange={handleOnChangeObservacao}
          label="Detalhes da operação"
          placeholder={placeholderObservacao}
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          disabled={mostrarSimulacao}
          inputProps={{ maxLength: 200 }}
        />
      </Box>
    </>
  );
});

InputsSessao.displayName = 'InputsSessao';
export default InputsSessao;
